// File: frontend/src/theme.ts
import { createTheme, alpha } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Design tokens — "cyber command center" design system.
// Single source of truth: pages must import these instead of hardcoding
// colors/gradients, so the palette stays consistent across the app.
// ---------------------------------------------------------------------------
export const cyber = {
  // Core palette (unchanged brand colors)
  primary: '#7b4cff',
  primaryLight: '#9d7bff',
  secondary: '#ff6f61',
  bgDefault: '#1a1d2e',
  bgPaper: '#242842',
  bgSidebar: '#1f2339',
  border: '#3a3f61',

  // Brand gradient built from the primary purple (replaces the legacy
  // #667eea → #764ba2 gradients scattered across pages)
  gradient: 'linear-gradient(135deg, #7b4cff 0%, #9d7bff 100%)',
  gradientHover: 'linear-gradient(135deg, #6a3df0 0%, #8c64ff 100%)',
  gradientText: {
    background: 'linear-gradient(90deg, #9d7bff 0%, #7b4cff 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  // Harmonized accent colors to break the purple monochrome. Use them to
  // emphasize analyst-relevant info: cyan for data/sources, green for
  // fresh/live/success, amber for caution, coral for highlights.
  accent: {
    cyan: '#4dd0e1',
    green: '#5dd39e',
    amber: '#fbbf24',
    coral: '#ff6f61',
  },

  // Glow effects for hover/focus accents
  glow: '0 0 0 1px rgba(123, 76, 255, 0.4), 0 4px 24px rgba(123, 76, 255, 0.25)',
  glowStrong: '0 0 0 1px rgba(123, 76, 255, 0.6), 0 8px 40px rgba(123, 76, 255, 0.35)',

  // Typography stacks
  fontUI: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  fontMono: '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace',
} as const;

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: cyber.primary,
      light: cyber.primaryLight,
    },
    secondary: {
      main: cyber.secondary,
    },
    info: {
      main: '#4dd0e1',
    },
    success: {
      main: '#5dd39e',
    },
    warning: {
      main: '#fbbf24',
    },
    error: {
      main: '#ff5c5c',
    },
    background: {
      default: cyber.bgDefault,
      paper: cyber.bgPaper,
    },
    divider: cyber.border,
    text: {
      primary: '#e6e8f0',
      secondary: '#9fa4c0',
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: cyber.fontUI,
    h3: { fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
    overline: { fontWeight: 700, letterSpacing: '0.12em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(cyber.primary, 0.25),
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
          '&:hover': {
            backgroundColor: alpha(cyber.primary, 0.45),
          },
        },
        '::selection': {
          backgroundColor: alpha(cyber.primary, 0.4),
        },
        code: {
          fontFamily: cyber.fontMono,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(cyber.bgDefault, 0.85),
          backdropFilter: 'blur(12px)',
          boxShadow: 'none',
          borderBottom: `1px solid ${cyber.border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: cyber.bgSidebar,
          borderRight: `1px solid ${cyber.border}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        containedPrimary: {
          background: cyber.gradient,
          boxShadow: '0 4px 16px rgba(123, 76, 255, 0.35)',
          '&:hover': {
            background: cyber.gradientHover,
            boxShadow: cyber.glow,
          },
        },
        outlinedPrimary: {
          borderColor: alpha(cyber.primary, 0.5),
          '&:hover': {
            borderColor: cyber.primary,
            backgroundColor: alpha(cyber.primary, 0.08),
            boxShadow: `0 0 12px ${alpha(cyber.primary, 0.25)}`,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${cyber.border}`,
          backgroundImage: 'none',
          transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
          '&:hover': {
            borderColor: alpha(cyber.primary, 0.6),
            boxShadow: cyber.glow,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: '#9fa4c0',
          borderBottom: `1px solid ${cyber.border}`,
        },
        root: {
          borderBottom: `1px solid ${alpha(cyber.border, 0.5)}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(cyber.primary, 0.04),
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: cyber.primary,
            boxShadow: `0 0 0 3px ${alpha(cyber.primary, 0.15)}`,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2c3052',
          border: `1px solid ${cyber.border}`,
          fontSize: '0.75rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: `1px solid ${cyber.border}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha(cyber.primary, 0.12),
        },
        bar: {
          borderRadius: 4,
          background: cyber.gradient,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});
